import React, { useState, useContext } from 'react';
import { Card, Typography, Avatar, Divider, Space, Tag, Button, Modal, Form, Input, Upload, message, Row, Col, Statistic } from 'antd';
import { UserOutlined, MailOutlined, GithubOutlined, LinkOutlined, EditOutlined, UploadOutlined, HeartOutlined, BookOutlined, TrophyOutlined, EyeOutlined } from '@ant-design/icons';
import { AuthContext } from '../../contexts/AuthContext';
import { defaultAboutInfoTemplate, getAllPosts, updateUser, getUserById } from '../../api/mockData'; // 添加新的导入
import './About.css';

const { Title, Text, Paragraph } = Typography;

const About = () => {
  const { user, updateUser: updateAuthUser } = useContext(AuthContext); // 获取更新用户的方法
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [qmauInfo, setQmauInfo] = useState({
    id: 1,
    name: 'Qmau',
    avatar: `${process.env.PUBLIC_URL}/qmau.jpg`, // 使用您实际的头像路径
    intro: '热爱技术，喜欢分享',
    details: '欢迎来到我的博客！我是一个热衷于前端开发的技术爱好者，专注于React、JavaScript等技术栈。在这里，我会分享技术心得、生活感悟以及各种有趣的项目。',
    email: 'Qmau@example.com',
    techStack: 'React, JavaScript, Node.js, Python, CSS',
    interests: '编程, 旅行, 摄影, 阅读, 音乐',
    github: 'https://github.com',
    linkedin: '#',
    personalHomepage: '#'
  });
  const [form] = Form.useForm();

  // 计算统计数据
  const allPosts = getAllPosts(); // 使用 getAllPosts 而不是 posts
  const totalPosts = allPosts.length;
  const totalViews = allPosts.reduce((sum, post) => sum + (post.views || 0), 0);
  const totalLikes = allPosts.reduce((sum, post) => sum + (post.likes || 0), 0);

  // 检查是否为qmau用户
  const isQmau = user && user.name === 'qmau';

  // 初始化时获取最新的qmau信息
  React.useEffect(() => {
    const loadQmauInfo = async () => {
      try {
        const qmauFromStorage = await getUserById(1); // qmau的id是1
        if (qmauFromStorage) {
          setQmauInfo(qmauFromStorage);
        }
      } catch (error) {
        console.error('加载Qmau信息失败:', error);
      }
    };
    
    loadQmauInfo();
  }, []);

  const handleEditProfile = () => {
    if (!user) {
      message.warning('请先登录');
      return;
    }
    
    // 只有qmau可以编辑About页面的信息
    if (isQmau) {
      // 编辑Qmau的信息
      const techStackStr = Array.isArray(qmauInfo.techStack) ? qmauInfo.techStack.join(', ') : qmauInfo.techStack;
      const interestsStr = Array.isArray(qmauInfo.interests) ? qmauInfo.interests.join(', ') : qmauInfo.interests;
      
      form.setFieldsValue({
        name: qmauInfo.name,
        intro: qmauInfo.intro,
        details: qmauInfo.details,
        email: qmauInfo.email,
        techStack: techStackStr,
        interests: interestsStr,
        github: qmauInfo.github,
        linkedin: qmauInfo.linkedin,
        personalHomepage: qmauInfo.personalHomepage,
        avatar: qmauInfo.avatar ? [{ uid: '-1', name: 'avatar.png', status: 'done', url: qmauInfo.avatar }] : [],
      });
      setIsModalVisible(true);
    } else {
      // 其他用户跳转到"我的"页面编辑自己的信息
      message.info('请前往"我的"页面编辑您的个人资料');
      setTimeout(() => {
        window.location.href = '/my';
      }, 1500);
    }
  };

  const handleSaveProfile = async (values) => {
    try {
      // 解析技术栈和兴趣爱好为数组
      const techStackArray = values.techStack ? values.techStack.split(',').map(item => item.trim()) : [];
      const interestsArray = values.interests ? values.interests.split(',').map(item => item.trim()) : [];

      // 更新qmau信息
      const updatedQmauData = {
        id: 1, // qmau的固定id
        name: values.name,
        intro: values.intro,
        details: values.details,
        email: values.email,
        techStack: techStackArray,
        interests: interestsArray,
        github: values.github,
        linkedin: values.linkedin,
        personalHomepage: values.personalHomepage,
        avatar: qmauInfo.avatar, // 保持当前头像，稍后处理头像上传
      };

      // 如果上传了新头像
      if (values.avatar && values.avatar.fileList && values.avatar.fileList.length > 0) {
        const file = values.avatar.fileList[0].originFileObj;
        if (file) {
          // 将文件转换为Base64
          const reader = new FileReader();
          reader.onload = async (e) => {
            updatedQmauData.avatar = e.target.result;
            
            try {
              // 保存更新后的用户信息
              await updateUser(updatedQmauData);
              // 更新本地状态
              setQmauInfo(updatedQmauData);
              message.success('Qmau资料更新成功');
              setIsModalVisible(false);
            } catch (error) {
              message.error('更新Qmau资料失败');
            }
          };
          reader.readAsDataURL(file);
        } else {
          // 如果没有新上传的头像，使用现有头像
          try {
            // 保存更新后的用户信息
            await updateUser(updatedQmauData);
            // 更新本地状态
            setQmauInfo(updatedQmauData);
            message.success('Qmau资料更新成功');
            setIsModalVisible(false);
          } catch (error) {
            message.error('更新Qmau资料失败');
          }
        }
      } else {
        // 保存更新后的用户信息
        await updateUser(updatedQmauData);
        // 更新本地状态
        setQmauInfo(updatedQmauData);
        message.success('Qmau资料更新成功');
        setIsModalVisible(false);
      }
    } catch (error) {
      console.error('更新Qmau资料失败:', error);
      message.error('更新Qmau资料失败');
    }
  };

  const uploadProps = {
    name: 'avatar',
    maxCount: 1,
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('只能上传 JPG/PNG 格式的图片!');
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('图片大小不能超过 2MB!');
      }
      return isJpgOrPng && isLt2M;
    },
  };

  return (
    <div className="about-page">
      {/* 头部卡片 - 个人信息 */}
      <Card className="profile-card header-card">
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} sm={8} md={6} lg={5} xl={4} style={{ textAlign: 'center' }}>
            <Avatar size={120} src={qmauInfo.avatar} icon={<UserOutlined />} />
          </Col>
          <Col xs={24} sm={16} md={18} lg={19} xl={20}>
            <div className="profile-header-content">
              <Title level={2} style={{ marginBottom: 8 }}>{qmauInfo.name}</Title>
              <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: 16 }}>
                <HeartOutlined style={{ marginRight: 8, color: '#eb2f96' }} />
                {qmauInfo.intro}
              </Text>
              <div className="social-links">
                <Space size="large">
                  <a href={qmauInfo.github} target="_blank" rel="noopener noreferrer">
                    <GithubOutlined style={{ fontSize: '24px' }} />
                  </a>
                  {qmauInfo.linkedin && qmauInfo.linkedin !== '#' && (
                    <a href={qmauInfo.linkedin} target="_blank" rel="noopener noreferrer">
                      <span style={{ fontSize: '24px' }}>in</span>
                    </a>
                  )}
                  {qmauInfo.personalHomepage && qmauInfo.personalHomepage !== '#' && (
                    <a href={qmauInfo.personalHomepage} target="_blank" rel="noopener noreferrer">
                      <LinkOutlined style={{ fontSize: '24px' }} />
                    </a>
                  )}
                </Space>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 统计数据卡片 */}
      <Card className="stats-card">
        <Row gutter={[16, 16]} justify="space-around">
          <Col xs={12} sm={6}>
            <Statistic 
              title="文章数量" 
              value={totalPosts} 
              prefix={<BookOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic 
              title="总阅读量" 
              value={totalViews} 
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic 
              title="总点赞数" 
              value={totalLikes} 
              prefix={<HeartOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic 
              title="粉丝数" 
              value={Math.floor(Math.random() * 500) + 50} 
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
        </Row>
      </Card>

      {/* 个人详情卡片 */}
      <Card className="profile-card details-card">
        <div className="profile-details">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={4} style={{ marginBottom: 16 }}>
                <span className="section-icon">📝</span> 个人简介
              </Title>
              <Paragraph style={{ fontSize: '16px', lineHeight: 1.8 }}>
                {qmauInfo.details}
              </Paragraph>
            </div>

            <div>
              <Title level={4} style={{ marginBottom: 16 }}>
                <span className="section-icon">💻</span> 技术栈
              </Title>
              <div className="tags-section tech-stack">
                {(Array.isArray(qmauInfo.techStack) ? qmauInfo.techStack : qmauInfo.techStack.split(',')).map((tech, index) => (
                  <Tag key={index} color="geekblue" style={{ fontSize: '14px', padding: '6px 12px' }}>
                    {tech.trim()}
                  </Tag>
                ))}
              </div>
            </div>

            <div>
              <Title level={4} style={{ marginBottom: 16 }}>
                <span className="section-icon">🎯</span> 兴趣爱好
              </Title>
              <div className="tags-section interests">
                {(Array.isArray(qmauInfo.interests) ? qmauInfo.interests : qmauInfo.interests.split(',')).map((interest, index) => (
                  <Tag key={index} color="gold" style={{ fontSize: '14px', padding: '6px 12px' }}>
                    {interest.trim()}
                  </Tag>
                ))}
              </div>
            </div>

            <div>
              <Title level={4} style={{ marginBottom: 16 }}>
                <span className="section-icon">📧</span> 联系方式
              </Title>
              <div className="contact-info">
                <Space direction="vertical" size="small">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <MailOutlined style={{ marginRight: 12, fontSize: '16px', color: '#1890ff' }} />
                    <Text strong>邮箱: </Text>
                    <Text copyable={{ text: qmauInfo.email }}>{qmauInfo.email}</Text>
                  </div>
                </Space>
              </div>
            </div>
          </Space>
        </div>
      </Card>

      {/* 编辑按钮 - 只有qmau用户才能看到 */}
      {isQmau && (
        <div className="edit-button-container">
          <Button 
            type="primary" 
            size="large"
            icon={<EditOutlined />}
            onClick={handleEditProfile}
            style={{ width: '200px' }}
          >
            编辑Qmau资料
          </Button>
        </div>
      )}

      {/* 编辑资料模态框 - 根据用户身份编辑不同资料 */}
      <Modal
        title={isQmau ? "编辑Qmau资料" : "编辑我的资料"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveProfile}
        >
          <Form.Item name="avatar" label="头像">
            <Upload {...uploadProps} listType="picture" className="avatar-uploader">
              <Button icon={<UploadOutlined />}>上传头像</Button>
            </Upload>
          </Form.Item>

          <Form.Item name="name" label="昵称" rules={[{ required: true, message: '请输入昵称' }]}>
            <Input placeholder="请输入昵称" />
          </Form.Item>

          <Form.Item name="intro" label="一句话介绍" rules={[{ required: true, message: '请输入一句话介绍' }]}>
            <Input placeholder="请输入一句话介绍" />
          </Form.Item>

          <Form.Item name="details" label="详细介绍">
            <Input.TextArea rows={4} placeholder="请输入详细介绍" />
          </Form.Item>

          <Form.Item name="email" label="邮箱" rules={[{ type: 'email', message: '请输入正确的邮箱格式' }]}>
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          <Form.Item name="techStack" label="技术栈">
            <Input placeholder="例如：React, Node.js, Python" />
          </Form.Item>

          <Form.Item name="interests" label="兴趣爱好">
            <Input placeholder="例如：编程, 旅行, 摄影" />
          </Form.Item>

          <Form.Item name="github" label="GitHub">
            <Input placeholder="请输入GitHub链接" />
          </Form.Item>

          <Form.Item name="linkedin" label="LinkedIn">
            <Input placeholder="请输入LinkedIn链接" />
          </Form.Item>

          <Form.Item name="personalHomepage" label="个人主页">
            <Input placeholder="请输入个人主页链接" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">保存</Button>
              <Button onClick={() => setIsModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default About;